import React, { Suspense, useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ApexChart from 'components/Charts';
import Spinner from 'components/Spinner';
import { formatNumberToCurrency, numberWithCommas } from 'helpers/utils';
import useRouter from 'hooks/useRouter';
import useUrlParams from 'hooks/useUrlParams';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import * as actions from 'store/actions';
import { config } from 'views/HomeScreen/chartConfig';

import useStyles from './styles';
import TableActualsForecasts from './TableActualsForecasts';
import TableSummary from './TableSummary';
import { Grid2, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ReportsSideMenu from './ReportSideMenu';
import { styled } from '@mui/material/styles';
import CashFlowTableSummary from './CashFlowTableSummary';
import CoreFilters from '../../CoreFilters';

const ASSET_AGGREGATED_CASH_FLOW_TABS = [
  'BaseRent', 'TurnoverRent', 'Opex', 'Capex', 'MLA', 'ServiceCharge', 'LeasingFee', 'FitOut'
];

const StyledToggleButton = styled(ToggleButton)({
    "&.Mui-selected, &.Mui-selected:hover": {
        color: "white",
        backgroundColor: '#3379FF'
    }
});

const namesMapper = {
    BaseRent: 'Base Rent',
    TurnoverRent: 'Turnover Rent',
    ServiceCharge: 'Service Charge',
    OtherIncome: 'Other Income',
    CumulativeRent: 'Cumulative Rent',
    NOI: 'NOI',
    Opex: 'Opex',
    Capex: 'Capex',
    OtherExpenses: 'Other Expenses',
    LeasingFee: 'Leasing Fee',
    FitOut: 'Fit Out',
    MLA: 'MLA'
};

const ChartDetails = () => {
    const year = useSelector((state) => state.home.yearFilter);
    const {data, error, loaded} = useSelector((state) => state.home.chartDataPage);
    const dispatch = useDispatch();
    const classes = useStyles();
    const {
        data: summaryData,
        loading: summaryDataLoading,
        loaded: summaryDataLoaded
    } = useSelector((state) => state.home.summaryData);
    const {data: forecastsData} = useSelector((state) => state.home.forecasts);
    const {data: actualsData} = useSelector((state) => state.home.actuals);
    const { params: { tab } } = useRouteMatch();
    const router = useRouter();
    const queryParam = new URLSearchParams(router.location.search);
    const [fromValue, setFromValue] = useState('');
    const [locationSearch, setLocationSearch] = useState('');
    const [period, setPeriod] = React.useState('month');

    const { portfolioIds, assetIds, assetType, assetsStatus } = useUrlParams();
    const [filter, setFilter] = useState({ portfolioIds, assetIds, assetTypes: assetType, assetsStatus });
    const [targetAssetIds, setTargetAssetIds] = useState([]);

    let defaultOptions = {...config.options}
    if (name === 'Tenants' || name === 'Occupancy') {
        defaultOptions = {
            ...defaultOptions,
            yaxis: {
                ...defaultOptions.yaxis,
                labels: {
                    formatter: (value) => `${numberWithCommas(value)}`
                }
            }
        };
    }
    const [options, setOptions] = useState(defaultOptions);
    useEffect(() => {
        if (period === 'year') {
            setOptions(prevOptions => ({
                ...prevOptions,
                xaxis: {
                    ...prevOptions.xaxis,
                    categories: new Array(5).fill().map((_, i) => year + i)
                }
            }));

        } else {
            setOptions(prevOptions => ({
                ...prevOptions,
                xaxis: {
                    ...prevOptions.xaxis,
                    categories: [
                        'Jan',
                        'Feb',
                        'Mar',
                        'Apr',
                        'May',
                        'Jun',
                        'Jul',
                        'Aug',
                        'Sep',
                        'Oct',
                        'Nov',
                        'Dec'
                    ]
                }
            }));
        }
    }, [period, year]);

    useEffect(() => {
        if (queryParam.get('from')) {
            setFromValue(queryParam.get('from'));
        }
    }, [fromValue]);

    useEffect(() => {
        if (router.location.search) {
            setLocationSearch(router.location.search);
        }
    }, [locationSearch]);

    useEffect(() => {
        const annualData = period === 'year' ? {year: year, length: 5} : null
        const yearFilter = period === 'month' ? [year] : new Array(5).fill().map((_, i) => year + i)

        const filters = {
            AssetType: filter.assetTypes,
            AssetStatus: filter.assetsStatus,
            PortfolioID: filter.portfolioIds,
            AssetID: filter.assetIds,
            Manager: filter.managers,
            Year: yearFilter
        };

        dispatch(actions.getFilterYears());
        dispatch(actions.getChartDataPerPage(tab, filters, period, annualData));
        dispatch(actions.getSummaryTableData(tab, filters, period, annualData));
    }, [filter, tab, year, period]);

    useEffect(() => {
      const annualData = period === 'year' ? {year: year, length: 5} : null
      const yearFilter = period === 'month' ? [year] : new Array(5).fill().map((_, i) => year + i)

      const filters = {
        AssetID: targetAssetIds,
        Year: yearFilter
      };

      dispatch(actions.getTenantsActuals(tab, filters, period, annualData));
      dispatch(
        actions.getTenantsForecasts(tab, filters, period, annualData)
      );
    }, [targetAssetIds, tab, year, period]);

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "scroll"
        };
    }, []);

    useEffect(() => {
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
    }, []);

    const handleSelectYear = (event, value) => {
        dispatch(actions.setYearFilter(value));
    };

    if (error && loaded) {
        return (
            <Typography color="error" style={{marginLeft: 30, marginTop: 30}}>
                {error}
            </Typography>
        );
    }

    let chartSourceData = tab !== 'MLA' ? summaryData?.totalforecastsList : summaryData?.mlasList;
    let chartFirstSeriesData = [];
    if (ASSET_AGGREGATED_CASH_FLOW_TABS.includes(tab)) {
      chartFirstSeriesData = calculateSummedByPeriod(chartSourceData ?? []);
    } else {
      chartFirstSeriesData = chartSourceData ?? [];
    }

    const chartSeries = [
      {
        name: `${namesMapper[tab]} Forecasts`,
        data: chartFirstSeriesData
      }
    ];
    if (tab !== 'MLA') {
      chartSeries.push({
        name: `${namesMapper[tab]} Actuals`,
        data: data?.actualforecastsList
      });
    }

    return (
        (
            <Grid2 container spacing={5} wrap="wrap" sx={{padding: '40px'}}>
                <Grid2 size={{lg: 3, xl: 2}}>
                    <Stack spacing={2}>
                        <Paper elevation={3} sx={{padding: '20px'}}>
                            <Stack spacing={2}>
                                <ToggleButtonGroup
                                    color="primary"
                                    value={year}
                                    exclusive
                                    onChange={handleSelectYear}
                                    fullWidth
                                >
                                    {new Array(3).fill().map((_, i) => {
                                        return new Date().getFullYear() - 2 + i
                                    }).map((item) => (
                                        <ToggleButton value={item}>{item}</ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                                <ToggleButtonGroup
                                  color="primary"
                                  value={period}
                                  exclusive
                                  onChange={(_, value) => setPeriod(value)}
                                  aria-label="Platform"
                                  sx={{
                                      alignItems: 'center',
                                      display: 'flex',
                                  }}
                                >
                                    <StyledToggleButton value="month">Monthly</StyledToggleButton>
                                    <StyledToggleButton value="year">Annual</StyledToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                        </Paper>
                        <ReportsSideMenu classes={classes} tab={tab}/>
                    </Stack>
                </Grid2>
                <Grid2
                    sx={{overflowY: 'scroll', height: 'calc(100vh - 120px)', overflowX: 'hidden'}}
                    size="grow">
                    <CoreFilters
                      filtersUpdated={setFilter}
                      targetAssetIdsUpdated={setTargetAssetIds}
                    />
                    <Stack spacing={2}>
                        <Paper>
                            <Box style={{padding: '20px', overflowX: 'hidden'}}>
                                <Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="h4">{namesMapper[tab]}</Typography>
                                    </Box>

                                    {tab !== 'MLA' && <Box className={classes.wrapCard}>
                                        <Typography variant="h6">
                                            {`Actuals: ${formatNumberToCurrency(
                                                data?.actualforecastsList?.reduce((acc, cur) => acc + cur, 0)
                                            )}`}
                                        </Typography>
                                    </Box>}
                                    <Box className={classes.wrapCard}>
                                        <Typography variant="h6">
                                            {`Total Forecasted: ${formatNumberToCurrency(
                                                data?.forecastsList?.reduce((acc, cur) => acc + cur, 0)
                                            )}`}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box>
                                    <Suspense fallback={<Spinner mini/>}>
                                        <ApexChart
                                            height={350}
                                            options={options}
                                            series={chartSeries}
                                        />
                                    </Suspense>
                                </Box>
                            </Box>
                          {
                            ASSET_AGGREGATED_CASH_FLOW_TABS.includes(tab) ?
                              (
                                (
                                  <CashFlowTableSummary
                                    chartType={tab}
                                    data={summaryData || null}
                                    period={period}
                                    yearFilter={year}
                                  />
                                )
                              ) :
                              (summaryData && (
                                <TableSummary
                                  data={summaryData || null}
                                  loading={summaryDataLoading}
                                  loaded={summaryDataLoaded}
                                  tab={tab}
                                  period={period}
                                  year={year}
                                />
                               )
                              )
                          }
                        </Paper>
                        <Paper>
                            <Box>
                              <Typography variant="h6" style={{ marginLeft: "1rem", marginTop: "0.5rem" }}>Forecasts</Typography>
                            </Box>
                            <Box>
                                <TableActualsForecasts
                                    data={forecastsData || []}
                                    name="forecastsList"
                                    tab={tab}
                                    period={period}
                                    year={year}
                                />
                            </Box>
                        </Paper>
                        {tab !== 'MLA' && (<Paper>
                            <Box>
                              <Typography variant="h6" style={{ marginLeft: "1rem", marginTop: "0.5rem" }}>Actuals</Typography>
                            </Box>
                            <Box>
                                <TableActualsForecasts
                                    data={actualsData || []}
                                    name="actualsList"
                                    tab={tab}
                                    period={period}
                                    year={year}
                                />
                            </Box>
                        </Paper>)}
                    </Stack>
                </Grid2>
            </Grid2>
        )
    );
};
export default ChartDetails;

function calculateSummedByPeriod(data) {
  const summed = data.reduce((acc, item) => {
    if (!acc[item.period]) {
      acc[item.period] = 0;
    }

    acc[item.period] += item.value;

    return acc;
  }, {});

  const sortedByPeriod = Object.entries(summed).sort((a, b) => a[0] - b[0]);
  return sortedByPeriod.map((it) => it[1]);
}